<template>
  <div v-if="hasActions" class="sheet-action-bar" :class="{ editing }">
    <div class="action-context">
      <span class="action-context-icon" aria-hidden="true">
        <b-icon :icon="editing ? 'pencil' : contextIcon" />
      </span>
      <div>
        <span class="action-context-title">{{ contextTitle }}</span>
        <span v-if="editing && editRowsHaveErrors" class="action-context-error">
          Fix the highlighted entries before saving.
        </span>
        <span v-else class="action-context-hint">{{ contextHint }}</span>
        <span
          v-if="unconfirmedWarning"
          class="action-context-warning"
          role="status"
          data-test="unconfirmed-days-warning"
        >
          <b-icon icon="exclamation-triangle" class="mr-1" aria-hidden="true" />
          {{ unconfirmedWarning }}
        </span>
      </div>
    </div>

    <div class="supervisor-actions">
      <template v-if="editing">
        <b-button
          variant="primary"
          size="sm"
          :disabled="loading || editRowsHaveErrors"
          @click="$emit('save')"
        >
          <b-spinner v-if="loading" small class="mr-1" />
          <b-icon v-else icon="check" class="mr-1" />
          {{ status === 'draft' ? 'Save Draft' : 'Save Changes' }}
        </b-button>
        <b-button
          v-if="status === 'draft'"
          variant="success"
          size="sm"
          :disabled="loading || editRowsHaveErrors"
          @click="$emit('submit')"
        >
          <b-spinner v-if="loading" small class="mr-1" />
          <b-icon v-else icon="send" class="mr-1" />
          Submit
        </b-button>
        <b-button
          variant="outline-secondary"
          size="sm"
          :disabled="loading"
          @click="$emit('cancel')"
        >
          Cancel
        </b-button>
      </template>

      <template v-else-if="status === 'submitted'">
        <b-button
          variant="success"
          size="sm"
          :disabled="loading"
          @click="$emit('approve')"
        >
          <b-spinner v-if="loading" small class="mr-1" />
          <b-icon v-else icon="check" class="mr-1" />
          Approve
        </b-button>
        <b-button
          variant="outline-danger"
          size="sm"
          :disabled="loading"
          @click="$emit('reject')"
        >
          <b-icon icon="x" class="mr-1" />
          Reject
        </b-button>
        <b-button
          variant="outline-secondary"
          size="sm"
          :disabled="loading"
          @click="$emit('edit')"
        >
          <b-icon-pencil class="mr-1" />
          Edit
        </b-button>
      </template>

      <template v-else-if="status === 'rejected'">
        <b-button
          variant="outline-secondary"
          size="sm"
          :disabled="loading"
          @click="$emit('edit')"
        >
          <b-icon-pencil class="mr-1" />
          Edit Timesheet
        </b-button>
      </template>

      <b-button
        v-else-if="status === 'approved' && canEditTimesheets"
        variant="outline-secondary"
        size="sm"
        :disabled="loading"
        @click="$emit('recall')"
      >
        <b-spinner v-if="loading" small class="mr-1" />
        <b-icon v-else icon="arrow-counterclockwise" class="mr-1" />
        Recall
      </b-button>

      <template v-else-if="status === 'draft'">
        <b-button
          v-if="canEditDrafts"
          variant="outline-secondary"
          size="sm"
          :disabled="loading"
          @click="$emit('edit')"
        >
          <b-icon-pencil class="mr-1" />
          Edit
        </b-button>
        <b-button
          v-if="canDeleteTimesheets"
          variant="danger"
          size="sm"
          :disabled="loading"
          @click="$emit('delete')"
        >
          <b-spinner v-if="loading" small class="mr-1" />
          <b-icon v-else icon="trash" class="mr-1" />
          Delete Draft
        </b-button>
      </template>
    </div>
  </div>
</template>

<script>
import { formatHours } from '@/utils/timesheetHelpers'

export default {
  name: 'SupervisorSheetActions',
  props: {
    status: { type: String, required: true },
    editing: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    editRowsHaveErrors: { type: Boolean, default: false },
    canEditTimesheets: { type: Boolean, default: false },
    canDeleteTimesheets: { type: Boolean, default: false },
    canEditDrafts: { type: Boolean, default: false },
    // From the API's unconfirmed_day_count / unconfirmed_minutes: future-dated
    // days still scheduled or pending confirmation. They count as 0 payroll
    // hours until confirmed (by design), so warn before approving. Never
    // blocks Approve.
    unconfirmedDayCount: { type: Number, default: 0 },
    unconfirmedMinutes: { type: Number, default: 0 },
  },
  computed: {
    unconfirmedWarning() {
      if (this.editing || this.status !== 'submitted') return ''
      const count = this.unconfirmedDayCount || 0
      if (count <= 0) return ''
      const hours = formatHours(this.unconfirmedMinutes)
      const days = count === 1 ? '1 day' : `${count} days`
      const verb = count === 1 ? 'is' : 'are'
      return `${days}${
        hours ? ` (${hours})` : ''
      } ${verb} not confirmed yet and will not count as payroll hours until confirmed.`
    },
    hasActions() {
      return (
        // Once editing has started, Save/Cancel must stay reachable
        // regardless of status/permission — otherwise a stale permission
        // prop would hide the bar mid-edit with no way to cancel.
        this.editing ||
        this.status === 'submitted' ||
        this.status === 'rejected' ||
        (this.status === 'approved' && this.canEditTimesheets) ||
        (this.status === 'draft' &&
          (this.canDeleteTimesheets || this.canEditDrafts))
      )
    },
    contextIcon() {
      if (this.status === 'approved') return 'arrow-counterclockwise'
      if (this.status === 'draft') return 'trash'
      if (this.status === 'rejected') return 'exclamation-circle'
      return 'clipboard-check'
    },
    contextTitle() {
      if (this.editing) return 'Editing timesheet'
      if (this.status === 'approved') return 'Approved timesheet'
      if (this.status === 'draft') return 'Draft timesheet'
      if (this.status === 'rejected') return 'Changes requested'
      return 'Ready for review'
    },
    contextHint() {
      if (this.editing) return 'Review each worker, then save your changes.'
      if (this.status === 'approved') {
        return 'Recall this timesheet to reopen it for changes.'
      }
      if (this.status === 'draft') {
        return this.canEditDrafts
          ? 'Take over this unfinished draft, or remove it.'
          : 'This unfinished submission can be removed.'
      }
      if (this.status === 'rejected') {
        return 'Edit the timesheet to address the rejection.'
      }
      return 'Approve, reject, or correct this submission.'
    },
  },
}
</script>

<style lang="scss" scoped>
.sheet-action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 16px;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f8fafc;
}

.sheet-action-bar.editing {
  border-color: #bfdbfe;
  background: #eff6ff;
}

.action-context {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.action-context-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #fff;
  color: #64748b;
  box-shadow: 0 0 0 1px #e2e8f0;
}

.editing .action-context-icon {
  color: #2563eb;
  box-shadow: 0 0 0 1px #bfdbfe;
}

.action-context-title,
.action-context-hint,
.action-context-error,
.action-context-warning {
  display: block;
}

.action-context-warning {
  margin-top: 6px;
  padding: 4px 8px;
  border: 1px solid #fcd34d;
  border-radius: 6px;
  background: #fffbeb;
  color: #92400e;
  font-size: 12px;
  font-weight: 600;
}

.action-context-title {
  color: #1f2937;
  font-size: 13px;
  font-weight: 700;
}

.action-context-hint,
.action-context-error {
  margin-top: 1px;
  font-size: 12px;
}

.action-context-hint {
  color: #6b7280;
}

.action-context-error {
  color: #b91c1c;
}

.supervisor-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.55rem;
  flex-shrink: 0;
}

.supervisor-actions .btn {
  min-width: 92px;
  border-radius: 7px;
  padding: 0.35rem 0.85rem;
  font-size: 12px;
  font-weight: 600;
}

@media (max-width: 767.98px) {
  .sheet-action-bar {
    align-items: stretch;
    flex-direction: column;
  }

  .supervisor-actions {
    width: 100%;
  }

  .supervisor-actions .btn {
    flex: 1 1 auto;
  }
}
</style>
